import React from "react"
import { motion, HTMLMotionProps } from "framer-motion"
import { cn } from "../../lib/utils"

interface PremiumButtonProps extends HTMLMotionProps<"button"> {
  children: React.ReactNode
  fullWidth?: boolean
  variant?: "primary" | "secondary"
}

export const PremiumButton = React.forwardRef<HTMLButtonElement, PremiumButtonProps>(
  ({ children, className, fullWidth, variant = "primary", disabled, ...props }, ref) => {
    return (
      <motion.button
        ref={ref}
        whileHover={!disabled ? { scale: 1.008, y: -0.5 } : {}}
        whileTap={!disabled ? { scale: 0.97, y: 0.5, transition: { duration: 0.18, ease: [0.25, 0.1, 0.25, 1] } } : {}}
        transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
        className={cn(
          // Base structure
          "relative group overflow-hidden font-medium transition-all duration-300 select-none",
          // Size and spacing
          "h-[52px] sm:h-[56px] text-[15px] sm:text-[16px] tracking-tight",
          // Width
          fullWidth ? "w-full" : "w-auto px-8",
          // Border radius - heavy, confident
          "rounded-2xl",
          // Primary variant - deep graphite with 3D effect + VISIBLY PREMIUM white glow
          variant === "primary" && [
            // Base color - deep graphite
            "bg-gradient-to-b from-[#0B0B0D] to-[#141416]",
            // Border - VISIBLE silver edge
            "border border-[rgba(255,255,255,0.25)]",
            // Shadow - VISIBLE elevated surface with strong inset highlight
            "shadow-[0_4px_12px_rgba(0,0,0,0.5),0_2px_4px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.25),inset_0_-2px_0_rgba(0,0,0,0.4)]",
            // Hover state - more lifted
            "hover:shadow-[0_6px_16px_rgba(0,0,0,0.55),0_3px_6px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.3),inset_0_-2px_0_rgba(0,0,0,0.4)]",
            // Active state - pressed down
            "active:shadow-[0_1px_3px_rgba(0,0,0,0.5),inset_0_3px_6px_rgba(0,0,0,0.5)]",
          ],
          // Secondary variant - VISIBLY premium white glow
          variant === "secondary" && [
            "bg-gradient-to-b from-[#0B0B0D] to-[#141416]",
            "border border-[rgba(255,255,255,0.12)]",
            "shadow-[0_2px_6px_rgba(0,0,0,0.35),0_1px_2px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.08)]",
            "hover:shadow-[0_3px_8px_rgba(0,0,0,0.4),0_2px_3px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.12)]",
            "active:shadow-[0_1px_3px_rgba(0,0,0,0.4),inset_0_2px_4px_rgba(0,0,0,0.3)]",
          ],
          // Disabled state - matte, no glow
          disabled && "opacity-40 cursor-not-allowed grayscale",
          className
        )}
        disabled={disabled}
        {...props}
      >
        {/* Top highlight - VISIBLE 3D "lip" effect */}
        {!disabled && (
          <div 
            className={cn(
              "absolute inset-x-0 top-0 h-[2px]",
              variant === "primary" ? "bg-gradient-to-r from-transparent via-[rgba(255,255,255,0.3)] to-transparent" : "bg-gradient-to-r from-transparent via-[rgba(255,255,255,0.15)] to-transparent"
            )} 
          />
        )}

        {/* Inner depth shadow - VISIBLE */}
        {!disabled && (
          <div className="absolute inset-0 rounded-2xl shadow-[inset_0_-2px_0_rgba(0,0,0,0.5)]" />
        )}

        {/* Subtle light reflection on hover */}
        {variant === "primary" && !disabled && (
          <div className="absolute inset-0 bg-gradient-to-b from-white/[0.03] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        )}

        {/* Soft light reflection sweep on hover - Apple-like */}
        {!disabled && (
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.08] to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
          </div>
        )}

        {/* Inner shadow - adds depth */}
        {!disabled && (
          <div className="absolute inset-0 rounded-2xl shadow-[inset_0_2px_0_rgba(0,0,0,0.4),inset_0_-1px_0_rgba(255,255,255,0.04)]" />
        )}

        {/* Content */}
        <span className={cn(
          "relative z-10 flex items-center justify-center gap-2.5 font-medium",
          variant === "primary" ? "text-white" : "text-white/80"
        )}>
          {children}
        </span>
      </motion.button>
    )
  }
)

PremiumButton.displayName = "PremiumButton"
